/*! Copyright (c) 2024, XAPPmedia */

/**
 * Options that can be passed when opening the form widget
 */
export interface OpenFormOptions {
    /**
     * Label of a chip to auto-select after the form loads.
     * If the first step of the form contains chips, the form will
     * automatically click the chip matching this label.
     */
    preselectChipLabel?: string;
}

// TypeScript type declarations for form-widget global APIs
declare global {
    interface Window {
        xafwControl?: {
            openForm: (options?: OpenFormOptions) => void;
            formStatus: string;
        };
    }
}

const FORM_WIDGET_SCRIPT_ID = "xapp-form-js";
const FORM_WIDGET_BASE_URL = "https://form.xapp.ai/xapp-form-widget.js";

/** Default retry configuration */
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_RETRY_DELAY_MS = 1000;

/**
 * Builds the form-widget script URL with the provided key
 */
export function getFormWidgetScriptUrl(key: string): string {
    return `${FORM_WIDGET_BASE_URL}?key=${encodeURIComponent(key)}`;
}

/**
 * Checks if the form-widget script is already loaded on the page.
 * Checks both by ID and by src URL to handle various installation methods.
 */
export function isFormWidgetScriptLoaded(key?: string): boolean {
    // Check by standard form-widget ID
    if (document.getElementById(FORM_WIDGET_SCRIPT_ID)) {
        return true;
    }

    // Check by src URL if key is provided
    if (key) {
        const scriptUrl = getFormWidgetScriptUrl(key);
        const scripts = document.querySelectorAll("script[src]");
        for (let i = 0; i < scripts.length; i++) {
            const src = scripts[i].getAttribute("src");
            if (src && (src === scriptUrl || src.startsWith(FORM_WIDGET_BASE_URL))) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Removes any failed script elements to allow retry
 */
function removeFailedScript(): void {
    const existingScript = document.getElementById(FORM_WIDGET_SCRIPT_ID);
    if (existingScript) {
        existingScript.remove();
    }
}

/**
 * Delays execution for a specified number of milliseconds
 */
function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Attempts to load the script once
 */
function attemptScriptLoad(key: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.id = FORM_WIDGET_SCRIPT_ID;
        script.src = getFormWidgetScriptUrl(key);
        script.async = true;

        script.onload = () => {
            resolve();
        };

        script.onerror = () => {
            reject(new Error(`Failed to load form-widget script: ${script.src}`));
        };

        document.head.appendChild(script);
    });
}

/**
 * Injects the form-widget script into the page with retry logic.
 * Returns a promise that resolves when the script is loaded.
 * Does nothing if the script is already loaded.
 *
 * @param key The form widget key
 * @param maxRetries Maximum number of retry attempts (default: 3)
 * @param retryDelayMs Delay between retries in milliseconds (default: 1000)
 */
export async function loadFormWidgetScript(
    key: string,
    maxRetries = DEFAULT_MAX_RETRIES,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS
): Promise<void> {
    // Check if already loaded
    if (isFormWidgetScriptLoaded(key)) {
        return;
    }

    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            // Clean up any failed script elements before retry
            if (attempt > 0) {
                removeFailedScript();
                console.log(`[chat-widget] Retrying form-widget script load (attempt ${attempt + 1}/${maxRetries + 1})`);
                await delay(retryDelayMs);
            }

            await attemptScriptLoad(key);
            return; // Success
        } catch (error) {
            lastError = error as Error;
            if (attempt === maxRetries) {
                console.error("[chat-widget] Failed to load form-widget script after all retries:", error);
            }
        }
    }

    throw lastError || new Error("Failed to load form-widget script");
}

/**
 * Pre-loads the form-widget script if a key is provided.
 * This is a fire-and-forget operation - errors are logged but not thrown.
 */
export function preloadFormWidgetScript(key?: string): void {
    if (!key) {
        return;
    }

    loadFormWidgetScript(key).catch((error) => {
        console.warn("[chat-widget] Failed to preload form-widget script:", error);
    });
}

/**
 * Checks if the form-widget control API is available
 */
export function isFormWidgetReady(): boolean {
    return typeof window.xafwControl?.openForm === "function";
}

/**
 * Opens the form widget if it's available.
 * Returns true if the form was opened, false otherwise.
 *
 * @param options Optional configuration for opening the form
 */
export function openFormWidget(options?: OpenFormOptions): boolean {
    if (isFormWidgetReady()) {
        window.xafwControl!.openForm(options);
        return true;
    }
    return false;
}

/**
 * Attempts to open the form widget, waiting for it to be ready if necessary.
 * Useful when the script is still loading.
 *
 * @param options Optional configuration for opening the form
 * @param maxAttempts Maximum number of attempts to check for readiness
 * @param intervalMs Time between attempts in milliseconds
 */
export function openFormWidgetWhenReady(
    options?: OpenFormOptions,
    maxAttempts = 20,
    intervalMs = 100
): Promise<boolean> {
    return new Promise((resolve) => {
        // Try immediately first
        if (openFormWidget(options)) {
            resolve(true);
            return;
        }

        let attempts = 0;
        const checkInterval = setInterval(() => {
            attempts++;
            if (openFormWidget(options)) {
                clearInterval(checkInterval);
                resolve(true);
                return;
            }
            if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                console.warn("[chat-widget] Form widget not ready after waiting");
                resolve(false);
            }
        }, intervalMs);
    });
}
